import * as React from 'react'
import { getSolidColor } from './utils'
import {
  BlockChange,
  type VariantSetBlock,
  Variant,
  withUniqueApiNames,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { TreeNode } from '@basehub/utils/tree/index'
import { InlineTextDiff } from '../rich-text/diff'
import { EyeClosedIcon } from '@radix-ui/react-icons'

export type VariantsDiff = {
  [id: VariantSetBlock['value']['variants'][number]['id']]:
    | {
        status: 'deleted'
        previous: Variant
        current?: never
      }
    | {
        status: 'modified'
        previous: Variant
        current: Variant
      }
    | {
        status: 'added'
        previous?: never
        current: Variant
      }
    | {
        status: 'unmodified'
      }
}

export const VariantSetBlockDiff = ({
  status,
  block,
  condition,
  variantsDiff,
}: {
  status: BlockChange['status']
  block: VariantSetBlock | null
  condition: 'removed' | 'added'
  variantsDiff: VariantsDiff
}) => {
  const variants = React.useMemo(() => {
    if (!block?.value.variants) return []
    let _variants = block.value.variants
    if (condition === 'removed') {
      _variants = _variants.filter(
        (v) => variantsDiff[v.id]?.status !== 'added'
      )
      const deletedVariants = Object.values(variantsDiff).reduce((acc, v) => {
        if (v.status === 'deleted') {
          acc.push(v.previous)
        }
        return acc
      }, [] as Variant[])
      _variants.push(...deletedVariants)
    }
    return withUniqueApiNames(_variants)
  }, [block?.value.variants, condition, variantsDiff])

  return (
    <div
      className={clsx(
        condition === 'added' && status === 'deleted' && 'opacity-30'
      )}
    >
      <div
        className={clsx(
          'border border-dark-border-solid rounded-lg py-[1.125rem] px-[1.375rem] bg-dark-base-dark grid gap-y-3 overflow-hidden',
          {
            '!border-red': condition === 'removed' && status === 'deleted',
            '!border-green': condition === 'added' && status === 'created',
          }
        )}
      >
        {variants.map((variant) => {
          const variantChange =
            status === 'created' || status === 'deleted'
              ? null
              : variantsDiff[variant.id]
          return (
            <VariantDisplayDiff
              condition={condition}
              key={variant.id}
              {...variant}
              change={variantChange}
            />
          )
        })}
      </div>
    </div>
  )
}

const VariantDisplayDiff = ({
  change,
  condition,
  ...props
}: Variant & {
  change: VariantsDiff[string] | null | undefined
  condition: 'removed' | 'added'
}) => {
  const { ApiNameText, LabelText } = React.useMemo(() => {
    if (change?.status === 'modified') {
      return {
        ApiNameText: () => (
          <InlineTextDiff
            condition={condition}
            text={change.current.apiName}
            textMatch={change.previous.apiName}
            isConflictingText={false}
          />
        ),
        LabelText: () => (
          <InlineTextDiff
            condition={condition}
            text={change.current.label}
            textMatch={change.previous.label}
            isConflictingText={false}
          />
        ),
      }
    }
    return {
      ApiNameText: () => props.apiName,
      LabelText: () => props.label,
    }
  }, [change, condition, props])

  const colorStyles: React.CSSProperties = React.useMemo(() => {
    if (
      change?.status === 'modified' &&
      change.current.color !== change.previous.color
    ) {
      return {
        border: '2px solid',
        borderColor: condition === 'added' ? '#46A758' : '#FF4949',
        backgroundColor: getSolidColor(
          condition === 'added' ? change.current.color : change.previous.color
        ),
      }
    }
    return {
      backgroundColor: getSolidColor(props.color),
      marginLeft: 2,
      marginRight: 2,
    }
  }, [change, condition, props.color])

  const hiddenDiff = React.useMemo(() => {
    const hasChanged =
      change?.status === 'modified' &&
      change.current.hidden !== change.previous.hidden
    return {
      hasChanged,
      value:
        hasChanged && condition === 'removed'
          ? change?.previous.hidden
          : props.hidden,
    }
  }, [change, condition, props.hidden])

  return (
    <div
      data-variant-item-diff
      className={clsx(
        'relative grid grid-cols-[auto_1fr_auto] grid-rows-[auto_auto] items-center gap-x-2 rounded-[1px] cursor-default text-left pointer-events-none',
        change?.status !== 'unmodified' &&
          'before:content-[""] before:absolute before:-top-1.5 before:-left-6 before:-bottom-1.5 before:-right-6',
        {
          'before:bg-green-diff/30': change?.status === 'added',
          'before:bg-red-diff/50': change?.status === 'deleted',
        }
      )}
    >
      <div
        className="size-[0.625rem] rounded-full relative box-content"
        style={colorStyles}
      />
      <span className="font-medium text-sm min-h-[1.3125rem] flex items-center relative">
        <LabelText />
        {props.isDefault && (
          <span className="ml-1.5 text-[0.6875rem] px-1 bg-dark-shade-hover inline-block leading-[1.125rem] rounded text-dark-faint uppercase font-medium">
            default
          </span>
        )}
      </span>
      <span className="col-start-2 text-dark-faint text-sm leading-normal relative">
        <ApiNameText />
      </span>
      {hiddenDiff.value && (
        <span
          className={clsx(
            'row-span-full col-start-[-2] self-center flex items-center gap-x-1.5 text-dark-faint',
            hiddenDiff.hasChanged &&
              condition === 'added' &&
              'bg-green-diff/30',
            hiddenDiff.hasChanged && condition === 'removed' && 'bg-red-diff/50'
          )}
        >
          <EyeClosedIcon />
          <span className="text-xs font-medium leading-normal">Hidden</span>
        </span>
      )}
    </div>
  )
}

export const getVariantsDiff = ({
  change,
}: {
  change: TreeNode<BlockChange>['value']
}) => {
  let diff: VariantsDiff = {}

  const previous = (change.conflicting ?? change.previous) as VariantSetBlock
  const current = change.current as VariantSetBlock
  if (!previous || !current) {
    return diff
  } else {
    const parsedCurrentVariants = withUniqueApiNames(current.value.variants)
    diff = withUniqueApiNames(previous.value.variants).reduce(
      (acc, variant) => {
        const match = parsedCurrentVariants.find((v) => v.id === variant.id)
        if (!match) {
          acc[variant.id] = {
            status: 'deleted',
            previous: variant,
          }
          return acc
        }

        if (variant.hidden !== match.hidden) {
          acc[variant.id] = {
            status: 'modified',
            previous: variant,
            current: match,
          }
          return acc
        }

        for (const key of [
          'label',
          'color',
          'apiName',
        ] satisfies (keyof Variant)[]) {
          if (variant[key] !== match[key]) {
            acc[variant.id] = {
              status: 'modified',
              previous: variant,
              current: match,
            }
            return acc
          }
        }

        acc[variant.id] = {
          status: 'unmodified',
        }
        return acc
      },
      diff
    )

    diff = withUniqueApiNames(current.value.variants).reduce((acc, variant) => {
      if (acc[variant.id]) return acc
      acc[variant.id] = {
        status: 'added',
        current: variant,
      }

      return acc
    }, diff)
  }
  return diff
}
